import Image from 'next/image'
import React, { ChangeEvent, useState, useRef } from 'react'
import { RiExpandDiagonalFill } from 'react-icons/ri'

import ImageFocusModal from '../private/image-focus-modal'

type TRadio = {
  readonly value: string
  readonly options: { title: string; value: string; answerImageUrl: string | null }[]
  readonly onChange: (value: string) => void
  readonly error?: string
}

export default function Radio({ value, options, onChange, error }: TRadio) {
  const [selectedValue, setSelectedValue] = useState<string>(value)
  const radiosRef = useRef<(HTMLInputElement | null)[]>([])
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [imageFocusDetails, setImageFocusDetails] = useState<{
    src: string | null
    alt: string | null
  }>({ src: null, alt: null })

  const hasImage = options.some((option) => option.answerImageUrl)

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setSelectedValue(value)
    onChange(value)
  }

  const handleClick = (index: number) => {
    if (radiosRef.current[index]) {
      radiosRef.current[index]?.click()
    }
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setImageFocusDetails({ src: null, alt: null })
  }

  const onClickExpand = ({
    e,
    src,
  }: {
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    src: string
  }) => {
    setIsModalOpen(true)
    setImageFocusDetails({
      src: src,
      alt: 'answer-option-image',
    })
    e.stopPropagation()
  }

  return (
    <div className={`${hasImage && 'md:grid-cols-2'} grid grid-cols-1 gap-5 overflow-hidden`}>
      {options.map((res, index) => (
        <div key={res.value}>
          <button
            type='button'
            className={`w-full cursor-pointer items-center rounded-lg border border-pop-gray-5 p-3 ${
              value === res.value ? 'border-[#329BFF] bg-[#E9F1FF]' : 'border-pop-gray-5'
            }`}
            onClick={() => handleClick(index)}
          >
            <div className={`mx-3 flex flex-row gap-2 ${hasImage && 'mb-3'}`}>
              <input
                type='radio'
                value={res.value}
                checked={selectedValue === res.value}
                onChange={handleOnChange}
                ref={(el) => {
                  if (el) {
                    radiosRef.current[index] = el
                  }
                }}
              />
              <span className='text-left'>{res.title}</span>
            </div>
            {hasImage && (
              <div className='relative flex h-[280px] w-full flex-row justify-center rounded-br-lg bg-neutral-200'>
                {res?.answerImageUrl && (
                  <>
                    <div className='relative h-[280px] w-full'>
                      <Image
                        src={res.answerImageUrl}
                        alt={`image-${res}`}
                        layout='fill'
                        objectFit='contain'
                        priority
                      />
                    </div>
                    <button
                      className='absolute bottom-0 right-0 rounded-br-lg bg-[#333333] p-2 text-white opacity-50'
                      type='button'
                      onClick={(e) => onClickExpand({ e, src: res.answerImageUrl ?? '' })}
                    >
                      <RiExpandDiagonalFill size={20} />
                    </button>
                  </>
                )}
              </div>
            )}
          </button>
        </div>
      ))}
      {!!error && <span className='w-full text-left text-xs text-pop-error'>{error}</span>}
      <ImageFocusModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        src={imageFocusDetails.src ?? ''}
        alt={imageFocusDetails.alt ?? ''}
      />
    </div>
  )
}
